// src/components/UserList.jsx
import React, { useState } from 'react';
import PermissionManager from './PermissionManager';
import { removeUser, hasPermission } from '../services/userManagementService';
import { PERMISSIONS, USER_ROLES } from '../types/permissions';
import '../styles/components/UserList.scss';

const UserList = ({ users, currentUser, accountId, isOwner, userPermissions, onUserUpdated }) => {
    const [selectedUser, setSelectedUser] = useState(null);
    const [showPermissionModal, setShowPermissionModal] = useState(false);
    const [loading, setLoading] = useState(false);

    const canEditPermissions = isOwner || hasPermission(userPermissions, PERMISSIONS.USERS_EDIT_PERMISSIONS);
    const canRemoveUsers = isOwner || hasPermission(userPermissions, PERMISSIONS.USERS_REMOVE);

    const handleEditPermissions = (user) => {
        if (user.role === USER_ROLES.ADMIN && !isOwner) {
            alert('管理ユーザーの権限は編集できません');
            return;
        }

        setSelectedUser(user);
        setShowPermissionModal(true);
    };

    const handleRemoveUser = async (user) => {
        if (user.uid === currentUser.uid) {
            alert('自分自身を削除することはできません');
            return;
        }

        if (user.role === USER_ROLES.ADMIN && !isOwner) {
            alert('管理ユーザーを削除する権限がありません');
            return;
        }

        if (!window.confirm(`${user.displayName || user.email} を削除しますか？`)) {
            return;
        }

        try {
            setLoading(true);
            await removeUser(accountId, user.id);
            onUserUpdated();
        } catch (error) {
            alert('ユーザーの削除に失敗しました: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handlePermissionUpdated = () => {
        setShowPermissionModal(false);
        setSelectedUser(null);
        onUserUpdated();
    };

    const getRoleLabel = (role) => {
        return role === USER_ROLES.ADMIN ? '管理ユーザー' : '編集ユーザー';
    };

    const getRoleClass = (role) => {
        return role === USER_ROLES.ADMIN ? 'role-admin' : 'role-editor';
    };

    if (users.length === 0) {
        return (
            <div className="user-list">
                <div className="empty-state">
                    <p>まだユーザーが登録されていません</p>
                </div>
            </div>
        );
    }

    return (
        <div className="user-list">
            <div className="users-table">
                <div className="table-header">
                    <div className="col-user">ユーザー</div>
                    <div className="col-role">ロール</div>
                    <div className="col-joined">参加日</div>
                    <div className="col-actions">操作</div>
                </div>

                {users.map(user => (
                    <div key={user.id} className="table-row">
                        <div className="col-user">
                            <div className="user-info">
                                <div className="user-name">
                                    {user.displayName || user.email}
                                    {user.uid === currentUser.uid && <span className="current-user">（あなた）</span>}
                                </div>
                                <div className="user-email">{user.email}</div>
                            </div>
                        </div>

                        <div className="col-role">
                            <span className={`role-badge ${getRoleClass(user.role)}`}>
                                {getRoleLabel(user.role)}
                            </span>
                        </div>

                        <div className="col-joined">
                            {user.joinedAt?.toDate?.()?.toLocaleDateString() || '未設定'}
                        </div>

                        <div className="col-actions">
                            {canEditPermissions && user.role === USER_ROLES.EDITOR && (
                                <button
                                    className="btn-secondary btn-small"
                                    onClick={() => handleEditPermissions(user)}
                                    disabled={loading}
                                >
                                    権限編集
                                </button>
                            )}

                            {canRemoveUsers && user.uid !== currentUser.uid && (
                                <button
                                    className="btn-danger btn-small"
                                    onClick={() => handleRemoveUser(user)}
                                    disabled={loading}
                                >
                                    削除
                                </button>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {showPermissionModal && selectedUser && (
                <PermissionManager
                    user={selectedUser}
                    accountId={accountId}
                    onClose={() => setShowPermissionModal(false)}
                    onPermissionUpdated={handlePermissionUpdated}
                />
            )}
        </div>
    );
};

export default UserList;




